import type { BlockDefinition, DraftSegment, EdgeDefinition, PatternSpecification, PointOperation, PointTuple } from "./types";

/**
 * A rigid move of the page: `[a, b, c, d, e, f]` maps (x, y) to
 * (a·x + c·y + e, b·x + d·y + f), as in SVG's matrix().
 */
export type Affine = [number, number, number, number, number, number];

export const IDENTITY: Affine = [1, 0, 0, 1, 0, 0];

/** A turn of `radians` about `pivot`, positive clockwise on the page (y down). */
export function rotation(pivot: PointTuple, radians: number): Affine {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return [cos, sin, -sin, cos, pivot[0] - cos * pivot[0] + sin * pivot[1], pivot[1] - sin * pivot[0] - cos * pivot[1]];
}

export function translation(dx: number, dy: number): Affine {
  return [1, 0, 0, 1, dx, dy];
}

/** `second` after `first`: apply `first`, then `second`. */
export function compose(second: Affine, first: Affine): Affine {
  const [a1, b1, c1, d1, e1, f1] = first;
  const [a2, b2, c2, d2, e2, f2] = second;
  return [
    a2 * a1 + c2 * b1,
    b2 * a1 + d2 * b1,
    a2 * c1 + c2 * d1,
    b2 * c1 + d2 * d1,
    a2 * e1 + c2 * f1 + e2,
    b2 * e1 + d2 * f1 + f2,
  ];
}

export function applyAffine(matrix: Affine, [x, y]: PointTuple): PointTuple {
  const [a, b, c, d, e, f] = matrix;
  return [a * x + c * y + e, b * x + d * y + f];
}

/**
 * Edge segments moved by `matrix`, running from `from` to `to`, which must be
 * the images of the source's ends (in either order). Throws if they are more
 * than `tolerance` mm off. Béziers stay Béziers: their controls move with them.
 */
export function transformSegments(segments: DraftSegment[], matrix: Affine, from: PointTuple, to: PointTuple, tolerance = 0.5): DraftSegment[] {
  const move = (point: PointTuple) => applyAffine(matrix, point);
  let moved: DraftSegment[] = segments.map((segment) => segment.kind === "line"
    ? { kind: "line", start: move(segment.start), end: move(segment.end) }
    : { kind: "bezier", start: move(segment.start), control1: move(segment.control1), control2: move(segment.control2), end: move(segment.end) });
  const gap = (a: PointTuple, b: PointTuple) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const first = moved[0].start;
  const last = moved[moved.length - 1].end;
  if (gap(first, to) <= tolerance && gap(last, from) <= tolerance && !(gap(first, from) <= tolerance && gap(last, to) <= tolerance)) {
    moved = [...moved].reverse().map((segment) => segment.kind === "line"
      ? { kind: "line", start: segment.end, end: segment.start }
      : { kind: "bezier", start: segment.end, control1: segment.control2, control2: segment.control1, end: segment.start });
  }
  if (gap(moved[0].start, from) > tolerance || gap(moved[moved.length - 1].end, to) > tolerance) {
    throw new Error("A moved edge's end points are not the images of its source's ends");
  }
  moved[0] = { ...moved[0], start: from } as DraftSegment;
  moved[moved.length - 1] = { ...moved[moved.length - 1], end: to } as DraftSegment;
  return moved;
}

/** The ID a transform gives the moved copy of a point or edge. */
export function movedId(id: string, suffix: string): string {
  return `${id}${suffix}`;
}

/**
 * Write out each block's `transforms` as the point operations and edges they
 * imply: every listed point, and both ends of every listed edge, get a
 * `transformPoint` copy (unless it `meets` an existing point); every listed
 * edge gets a `transformEdge` copy joining the moved ends. The copies are
 * named `<id><suffix>`.
 *
 * Idempotent: a copy already written out (same operation, source and
 * transform) is left alone. A different element with the same ID is kept as
 * well, so validation reports the clash.
 */
export function expandTransforms<T extends Partial<PatternSpecification>>(specification: T): T {
  if (!specification || typeof specification !== "object") return specification;
  const blocks = specification.blocks;
  if (!blocks || typeof blocks !== "object") return specification;
  let changed = false;
  const expanded: Record<string, BlockDefinition> = {};
  for (const [blockId, block] of Object.entries(blocks)) {
    const transforms = block?.transforms;
    if (!transforms || typeof transforms !== "object" || Object.keys(transforms).length === 0) {
      expanded[blockId] = block;
      continue;
    }
    const operations: PointOperation[] = Array.isArray(block.operations) ? [...block.operations] : [];
    const edges: Record<string, EdgeDefinition> = { ...block.edges };
    const pointWritten = (id: string, from: string, transform: string) => operations.some((operation) => operation?.id === id && operation.operation === "transformPoint" && operation.from === from && operation.transform === transform);
    let blockChanged = false;
    for (const [transformId, transform] of Object.entries(transforms)) {
      if (!transform || typeof transform !== "object" || typeof transform.suffix !== "string" || transform.suffix === "") continue;
      const suffix = transform.suffix;
      const sources: string[] = [];
      const carry = (id: unknown) => {
        if (typeof id === "string" && id !== "" && !sources.includes(id)) sources.push(id);
      };
      for (const id of Array.isArray(transform.points) ? transform.points : []) carry(id);
      const edgeIds = Array.isArray(transform.edges) ? transform.edges : [];
      for (const edgeId of edgeIds) {
        const source = edges[edgeId];
        carry(source?.from);
        carry(source?.to);
      }
      const meets: Record<string, string> = {};
      if (transform.rotate && typeof transform.rotate.around === "string" && transform.follow === undefined) meets[transform.rotate.around] = transform.rotate.around;
      if (transform.meets && typeof transform.meets === "object") Object.assign(meets, transform.meets);
      const image = (id: string) => Object.hasOwn(meets, id) ? meets[id] : movedId(id, suffix);
      for (const id of sources) {
        if (Object.hasOwn(meets, id)) continue;
        const target = movedId(id, suffix);
        if (pointWritten(target, id, transformId)) continue;
        operations.push({ id: target, operation: "transformPoint", from: id, transform: transformId, source: transform.label ?? `${transformId}: ${id} moved` });
        blockChanged = true;
      }
      for (const edgeId of edgeIds) {
        const source = edges[edgeId];
        if (typeof edgeId !== "string" || !source) continue;
        const target = movedId(edgeId, suffix);
        const existing = edges[target];
        if (existing?.operation === "transformEdge" && existing.edge === edgeId && existing.transform === transformId) continue;
        if (existing) {
          // Keep the author's edge; validation reports the clash.
          continue;
        }
        const when = [transform.when, source.when].filter((condition): condition is string => typeof condition === "string" && condition !== "");
        edges[target] = {
          operation: "transformEdge",
          edge: edgeId,
          transform: transformId,
          from: image(source.from),
          to: image(source.to),
          ...(when.length === 1 ? { when: when[0] } : when.length > 1 ? { when: `and(${when.join(", ")})` } : {}),
        };
        blockChanged = true;
      }
    }
    if (blockChanged) {
      changed = true;
      expanded[blockId] = { ...block, operations, edges };
    } else {
      expanded[blockId] = block;
    }
  }
  return changed ? { ...specification, blocks: expanded } : specification;
}
